import { useState, useEffect } from 'react';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Capacitor } from '@capacitor/core';
import { initIAP, isIapElite, addEliteListener } from '../lib/iap';
import { isGrantActive, parseGrantExpiry } from '../lib/entitlement';

/** Which source(s) currently grant Elite. Entitlement is the OR of all three, but
 *  WHICH one matters for billing: an Apple subscription is cancelled in iOS
 *  Settings, a Stripe one only on the web, and a comped grant has no billing at
 *  all. Sending a coach to the wrong place is how "I can't cancel" happens. */
export interface EliteSources {
  stripe: boolean;
  iap: boolean;
  grant: boolean;
}

interface SubscriptionState {
  isElite: boolean;
  isLoading: boolean;
  seenTrialOffer: boolean;
  eliteSources: EliteSources;
  /** When the current grant lapses, epoch ms — or null for no grant AND for a
   *  founding comp, which never expires. The UI uses this to tell a Season Pass
   *  holder when their pass ends; a founding coach must never be shown an end
   *  date, and null is what keeps those two apart. */
  grantExpiresAt: number | null;
}

export function useSubscription(uid: string | undefined): SubscriptionState {
  const [isElite, setIsElite] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [seenTrialOffer, setSeenTrialOffer] = useState(false);
  const [eliteSources, setEliteSources] = useState<EliteSources>({ stripe: false, iap: false, grant: false });
  const [grantExpiresAt, setGrantExpiresAt] = useState<number | null>(null);

  useEffect(() => {
    if (!uid) {
      setIsElite(false);
      setIsLoading(false);
      setSeenTrialOffer(false);
      setEliteSources({ stripe: false, iap: false, grant: false });
      setGrantExpiresAt(null);
      return;
    }

    let grantExpiry: number | null = null;
    let manualGrant = false;
    let stripeActive = false;
    let iapActive = false;
    let seenOffer = false;
    let userDocReady = false;
    let stripeReady = false;
    // On web there is no IAP, so the IAP source is considered "ready" immediately.
    let iapReady = !Capacitor.isNativePlatform();
    let expiryTimer: ReturnType<typeof setTimeout> | null = null;

    /** Re-resolve exactly when a grant lapses, so Elite drops mid-session rather
     *  than lingering until the next cold start. setTimeout overflows past ~24.8
     *  days, so anything further out is left to the next app load. */
    function armGrantExpiryTimer(expiresAt: number | null) {
      if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = null; }
      if (expiresAt === null) return;
      const ms = expiresAt - Date.now();
      if (ms <= 0 || ms > 20 * 24 * 60 * 60 * 1000) return;
      expiryTimer = setTimeout(() => {
        manualGrant = false;
        resolve();
      }, ms);
    }

    function resolve() {
      if (userDocReady && stripeReady && iapReady) {
        // Native: a web Stripe subscriber reads through with no Apple cut —
        // entitlement is the OR of all three sources.
        setIsElite(manualGrant || stripeActive || iapActive);
        setSeenTrialOffer(seenOffer);
        setEliteSources({ stripe: stripeActive, iap: iapActive, grant: manualGrant });
        // Only surface an expiry while the grant is what's actually granting Elite.
        // A founding comp has no expiry, and a Stripe/IAP subscriber's own billing
        // date is not this field.
        setGrantExpiresAt(manualGrant ? grantExpiry : null);
        setIsLoading(false);
      }
    }

    // 1. users/{uid}.tier — the server-written grant. Two shapes share this field:
    //
    //      tier: 'elite', no eliteGrantExpiresAt   → founding comp, NEVER expires
    //      tier: 'elite', eliteGrantExpiresAt set  → time-limited trial grant
    //
    //    That is the same rule isFoundingCoach() applies in functions/src/index.ts,
    //    and the absence of an expiry meaning "forever" is what keeps every existing
    //    founding coach untouched by this check.
    //
    //    The listener cannot fire on time merely passing, so an expiring grant also
    //    arms a timer to re-resolve at the moment it lapses. Without it a coach whose
    //    grant ran out mid-session would keep Elite until the next cold start.
    const userUnsub = onSnapshot(doc(db, 'users', uid), (snap) => {
      const d = snap.exists() ? snap.data() : null;
      manualGrant = isGrantActive(d);
      seenOffer = !!d?.seenTrialOffer;
      userDocReady = true;
      grantExpiry = parseGrantExpiry(d?.eliteGrantExpiresAt);
      armGrantExpiryTimer(grantExpiry);
      resolve();
    });

    // 2. Check customers/{uid}/subscriptions for active Stripe subscription
    const subsQuery = query(
      collection(db, 'customers', uid, 'subscriptions'),
      where('status', 'in', ['active', 'trialing'])
    );
    const stripeUnsub = onSnapshot(subsQuery, (snap) => {
      stripeActive = !snap.empty;
      stripeReady = true;
      resolve();
    }, () => {
      // Permission denied or rules not yet deployed — treat as no active subscription
      stripeReady = true;
      resolve();
    });

    // 3. Native only: RevenueCat IAP entitlement (Apple subscriptions). Init,
    //    read the current state once, then keep it live so purchase/restore/
    //    renewal/expiry flow through to isElite without a reload.
    let iapUnsub: () => void = () => {};
    if (Capacitor.isNativePlatform()) {
      initIAP(uid)
        .then(isIapElite)
        .then((active) => {
          iapActive = active;
          iapReady = true;
          resolve();
        })
        .catch(() => {
          iapReady = true;
          resolve();
        });
      iapUnsub = addEliteListener((active) => {
        iapActive = active;
        resolve();
      });
    }

    return () => {
      userUnsub();
      stripeUnsub();
      iapUnsub();
      // A pending expiry timer would otherwise fire after unmount (or after a
      // sign-out into a different account) and call setState on a dead hook.
      if (expiryTimer) clearTimeout(expiryTimer);
    };
  }, [uid]);

  return { isElite, isLoading, seenTrialOffer, eliteSources, grantExpiresAt };
}
